<script setup lang="ts">
import { Entity, SysOrgGroup } from "~/api";
import { ref } from "vue";
import { ElMessageBox } from "element-plus";
import OrgGroupEditor from "./OrgGroupEditor.vue";

type mainType = SysOrgGroup;

const loading = ref(false);
const props = defineProps<{
  orgGroup: SysOrgGroup;
  availableEntities: Entity[];
  drawerOpen: boolean;
  addingNewOrgGroup: boolean;
}>();

const emit = defineEmits<{
  (e: "drawerClosed"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
  (e: "saveNeeded"): void;
}>();

const orgGroup = ref(props.orgGroup);

const saveNeeded = ref(false);

const activeTabName = ref("general");

const handleNewOrgGroupAdded = (item: mainType) => {
  orgGroup.value = item;
  emit("added", item);
  emit("drawerClosed");
};

const handleOrgGroupUpdated = (item: mainType) => {
  // passthrough
  orgGroup.value = item;
  emit("updated", item);
  saveNeeded.value = false;
};

const handleOrgGroupDeleted = (item: mainType) => {
  orgGroup.value = item;
  emit("deleted", item);
  emit("drawerClosed");
};

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        emit("drawerClosed");
      });
  } else {
    emit("drawerClosed");
  }
};
</script>

<template>
  <el-drawer
    v-if="orgGroup"
    :model-value="drawerOpen"
    :title="orgGroup.name || '\u00A0'"
    :destroy-on-close="true"
    :before-close="handleClose"
    direction="rtl"
    size="80%"
  >
    <div v-loading="loading" class="item-designer">
      <el-tabs v-model="activeTabName" type="card" class="demo-tabs">
        <el-tab-pane label="General" name="general">
          <OrgGroupEditor
            v-if="orgGroup"
            :key="orgGroup.guid"
            :org-group="orgGroup"
            :available-entities="availableEntities"
            :drawer-open="drawerOpen"
            :adding-new-org-group="addingNewOrgGroup"
            @added="handleNewOrgGroupAdded"
            @updated="handleOrgGroupUpdated"
            @deleted="handleOrgGroupDeleted"
            @drawer-closed="handleClose"
            @save-needed="saveNeeded = true"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<style lang="less" scoped></style>
